import { zodResolver } from '@hookform/resolvers/zod';
import { Image } from 'expo-image';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { Car } from '@/api/cars/types';
import {
  NO_VIN_TRIM_MESSAGE,
  selectedTrimId,
} from '@/api/partners/cars/use-car-detail-options';
import { getCarImages, isDesktop } from '@/core';
import { Button, Card, Checkbox, ControlledInput, Text } from '@/ui';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';

export const carDetailsFormSchema = z.object({
  id: z.number(),
  defaultLocationId: z.string().min(1, 'Pickup location is required'),
  trimId: z.string().min(1, 'Trim is required'),
  licensePlate: z.string().min(1, 'License plate is required'),
  licensePlateState: z.string().min(2, 'License plate state is required'),
  gloveboxPin: z.string().min(4, 'Glovebox pin is required'),
});

export type CarDetailsFormType = z.infer<typeof carDetailsFormSchema>;

export default function CarDetailsForm({
  pickupLocationOptions,
  trimOptions,
  trimLocked = false,
  licensePlateStateOptions,
  car,
  selected,
  index,
  onChange,
  onValidChange,
  onAddNewLocation,
  style,
  defaultValues,
  onSelectChange,
}: {
  pickupLocationOptions: DropdownOption[];
  trimOptions: DropdownOption[];
  trimLocked?: boolean;
  licensePlateStateOptions: DropdownOption[];
  car: Car;
  selected: boolean;
  index: number;
  onChange: (index: number, values: CarDetailsFormType) => void;
  onValidChange: (index: number, isValid: boolean) => void;
  onAddNewLocation: () => void;
  style?: any;
  defaultValues?: Partial<CarDetailsFormType>;
  onSelectChange?: (selected: boolean) => void;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const [checked, setChecked] = useState(selected);

  useEffect(() => {
    setChecked(selected);
  }, [selected]);

  const {
    control,
    trigger,
    setValue,
    formState: { isValid, errors },
    getValues,
    reset,
    watch,
  } = useForm<CarDetailsFormType>({
    defaultValues: {
      id: car.id,
      defaultLocationId:
        defaultValues?.defaultLocationId ??
        car.defaultLocation?.id.toString() ??
        undefined,
      trimId: defaultValues?.trimId ?? car.trim?.id.toString() ?? undefined,
      licensePlate: defaultValues?.licensePlate ?? car.licensePlate ?? '',
      licensePlateState:
        defaultValues?.licensePlateState ?? car.licensePlateState ?? '',
      gloveboxPin: defaultValues?.gloveboxPin ?? car.gloveboxPin ?? '',
    },
    resolver: zodResolver(carDetailsFormSchema),
  });

  const [licensePlateState, trimId, defaultLocationId] = useWatch({
    control,
    name: ['licensePlateState', 'trimId', 'defaultLocationId'],
  });

  useEffect(() => {
    const nextTrimId = selectedTrimId({
      options: trimOptions,
      locked: trimLocked,
      currentTrimId: trimId,
    });

    if ((trimId ?? '') === nextTrimId) return;

    setValue('trimId', nextTrimId);
    trigger('trimId');
    onChange(index, { ...getValues(), trimId: nextTrimId });
  }, [
    trimLocked,
    trimOptions,
    trimId,
    setValue,
    trigger,
    getValues,
    index,
    onChange,
  ]);

  useEffect(() => {
    onValidChange(index, isValid);
  }, [isValid]);

  useEffect(() => {
    if (defaultValues) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent when values change
  useEffect(() => {
    const sub = watch(() => {
      onChange(index, getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  return (
    <Card style={[styles.container, style]}>
      <View
        style={{
          flexDirection: 'row',
          gap: 8,
          marginTop: 24,
          width: isDesktop() ? 300 : 'auto',
        }}
      >
        <Checkbox
          active={checked}
          onPress={() => {
            setChecked((prev) => !prev);
            onSelectChange?.(!checked);
          }}
        />

        <Image
          source={{
            uri:
              car && getCarImages(car.images).length > 0
                ? getCarImages(car.images)[0].url
                : '',
          }}
          style={{ width: 70, height: 48 }}
          contentFit="cover"
        />

        <View>
          <Text type="bodyRegular">
            {car.vehicleYear && car.brandName && car.modelName
              ? `${car.vehicleYear} ${car.brandName} ${car.modelName}`
              : 'Loading Car Information...'}
          </Text>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {car.vin}
          </Text>
        </View>
      </View>

      <Dropdown
        mode="single"
        label="Trim"
        options={trimOptions}
        disabled={trimLocked}
        placeHolderText="Select car's trim"
        onSelect={(selected: DropdownOption[]) => {
          setValue('trimId', selected[0].id);
          trigger();
        }}
        onClear={() => {
          setValue('trimId', '');
          trigger('trimId');
        }}
        values={trimId ? [trimId] : []}
        error={
          trimOptions.length === 0
            ? NO_VIN_TRIM_MESSAGE
            : errors.trimId?.message
        }
      />

      <Dropdown
        mode="single"
        label="License Plate State"
        options={licensePlateStateOptions}
        placeHolderText="Select license plate state"
        onSelect={(selected: DropdownOption[]) => {
          setValue('licensePlateState', selected[0].value);
          trigger();
        }}
        onClear={() => {
          setValue('licensePlateState', '');
          trigger('licensePlateState');
        }}
        values={licensePlateState ? [licensePlateState] : []}
        error={errors.licensePlateState?.message}
      />

      <View>
        <ControlledInput
          control={control}
          name="licensePlate"
          label="License Plate"
          onBlur={() => {
            trigger('licensePlate');
          }}
          onChangeText={(text) => {
            const upperText = text.toUpperCase();
            setValue('licensePlate', upperText);
            trigger('licensePlate');
          }}
          containerStyle={styles.inputContainer}
        />
      </View>

      <View>
        <ControlledInput
          control={control}
          name="gloveboxPin"
          label="Glovebox Pin"
          onBlur={() => {
            trigger('gloveboxPin');
          }}
          onChangeText={(text) => {
            setValue('gloveboxPin', text);
            trigger('gloveboxPin');
          }}
          tooltipText="For added security, we require you to keep the key card in the glovebox and set a glovebox PIN."
          containerStyle={styles.inputContainer}
        />
      </View>

      <Dropdown
        mode="single"
        label="Pickup Location"
        options={pickupLocationOptions}
        placeHolderText="Select your car's pickup location"
        onSelect={(selected: DropdownOption[]) => {
          setValue('defaultLocationId', selected[0].id);
          trigger();
        }}
        onClear={() => {
          setValue('defaultLocationId', '');
          trigger('defaultLocationId');
        }}
        values={defaultLocationId ? [defaultLocationId] : []}
        error={errors.defaultLocationId?.message}
      />

      <Button
        text="Add location"
        type="secondaryExtraSmall"
        onPress={onAddNewLocation}
        style={styles.addLocationButton}
      />
    </Card>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    gap: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    backgroundColor: {
      xs: theme.colors.card.default.backgroundColor,
      md: 'transparent',
    },
    borderWidth: {
      md: 0,
    },
  },
  inputContainer: {
    maxWidth: {
      md: 250,
    },
  },
  addLocationButton: {
    width: 120,
    alignSelf: {
      md: 'center',
    },
  },
}));
